import decodeJwt from 'jwt-decode';
import { CRUD, ProjectType, ROLE, RoleNames } from '../types';

interface AuthInfo {
  user_name: string;
  role_name: string;
  user_id?: string;
  user_email?: string;
  user_type?: string;
}

const COOKIE_NAME = 'ntoken';
const SESSION_TTL_SEC = 10800; // 3시간

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, maxAge: number) {
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
}

function removeCookie(name: string) {
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/** 쿠키에서 ntoken을 읽어 JWT decode */
function getTokenPayload(): AuthInfo | null {
  const token = getCookie(COOKIE_NAME);
  if (!token) return null;
  try {
    const payload: AuthInfo = decodeJwt(token);
    if (payload.user_name && payload.role_name) return payload;
    return null;
  } catch {
    return null;
  }
}

/** 쿠키에서 ntoken raw 값 반환 (API 호출용) */
export function getToken(): string | null {
  return getCookie(COOKIE_NAME);
}

/**
 * 토큰 확인 및 쿠키 세팅
 * - 로컬 개발: ?ntoken= 쿼리 파라미터 → 쿠키에 저장
 * - 배포 환경: CloudFront가 이미 쿠키 세팅 완료
 */
export function checkToken(ntokenParam: string | null) {
  // 1) 쿼리 파라미터로 받은 토큰 → 쿠키에 저장 (로컬 개발)
  if (ntokenParam) {
    setCookie(COOKIE_NAME, ntokenParam, SESSION_TTL_SEC);
    const location = localStorage.getItem('location') || '/';
    localStorage.removeItem('location');
    window.location.href = location;
    return;
  }

  // 2) 쿠키에 토큰이 있으면 정상 통과
  if (getTokenPayload()) {
    return;
  }

  // 3) 토큰 없음 → auth 사이트로 리다이렉트
  localStorage.setItem('location', window.location.pathname);
  window.location.href = 'https://auth.nexstreaming.com/auth';
}

/**
 * 인증 정보 조회 — 쿠키의 JWT에서 직접 읽음
 * key: 'role' | 'name' | 'email'
 */
export default function getAuth(key: string): string | null {
  const payload = getTokenPayload();
  if (!payload) return null;

  const keyMap: Record<string, string | undefined> = {
    role: payload.role_name,
    name: payload.user_name,
    email: payload.user_email,
  };
  return keyMap[key] ?? null;
}

export function signOut() {
  removeCookie(COOKIE_NAME);
  localStorage.removeItem('location');
  window.location.href = window.location.origin + window.location.pathname;
}

export function checkAuth(projectType: ProjectType, actionType: CRUD) {
  const role = getAuth('role') as RoleNames;
  if (
    Object.keys(ROLE).includes(role) &&
    ROLE[role][actionType].includes(projectType)
  ) {
    return true;
  } else {
    return false;
  }
}
